import React, { useState } from 'react';
import usePDA1040_R01Store from './usePDA1040_R01Store';
import { useBarcodeScan } from 'src/hooks/useBarcodeScan';
import useUiuxStore from 'src/store/useUiuxStore';

interface PDA1040_R01InfoProps {
  onScanBarcode: () => Promise<boolean>;
  onCheck: () => boolean;
  onComplete: () => void;
  checkedCount?: number;
}

const PDA1040_R01Info: React.FC<PDA1040_R01InfoProps> = ({
  onScanBarcode,
  onCheck,
  onComplete,
  checkedCount = 0,
}) => {
  const {
    selectedOrder,
    barcode,
    setBarcode,
    scannedItem,
    transportQty,
    setTransportQty,
    warehouseCd,
    setWarehouseCd,
    warehouseNm,
    setWarehouseNm,
    remark,
    setRemark,
  } = usePDA1040_R01Store();

  const { setCodeSearchModalVisible, setCodeSearchModalProps } = useUiuxStore();
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);

  // 바코드 스캔 핸들러
  const handleBarcodeScan = async (scannedBarcode: string) => {
    setBarcode(scannedBarcode);
    const success = await onScanBarcode();
    if (success) {
      setTimeout(() => {
        const qtyInput = document.getElementById('transportQty') as HTMLInputElement;
        qtyInput?.focus();
      }, 100);
    }
  };

  const { scanInputRef, handleScanKeyDown, handleInput } = useBarcodeScan(
    handleBarcodeScan,
    {
      clearOnScan: false,
      refocusOnScan: true,
      debounceMs: 100,
      autoFocus: true,
    }
  );

  const handleDoubleTap = () => {
    const now = Date.now();
    const timeDiff = now - lastTapTime;
    
    if (timeDiff < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }
    
    setLastTapTime(now);
  };

  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  const focusScanInput = () => {
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleCheck = () => {
    const success = onCheck();
    if (success) {
      focusScanInput();
    }
  };

  const handleCheckKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCheck();
    }
  };

  // 창고 검색 모달 열기
  const openWarehouseSearchModal = () => {
    if (!selectedOrder) return;

    setCodeSearchModalProps({
      category: '창고코드',
      search: '', // 빈 값으로 초기화하여 재선택 가능
      autoSelectSingle: true,
      autoFocus: false, // 자동 포커스 비활성화
      onConfirm: (selectedWarehouse: any) => {
        setWarehouseCd(selectedWarehouse.warehouseCd);
        setWarehouseNm(selectedWarehouse.warehouseNm);
        setCodeSearchModalVisible(false);
        // 포커스를 입고량으로 이동
        setTimeout(() => {
          document.getElementById('transportQty')?.focus();
        }, 100);
      },
      onCloseConfirm: () => {
        setCodeSearchModalVisible(false);
      }
    });
    setCodeSearchModalVisible(true);
  };

  const handleWarehouseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      openWarehouseSearchModal();
    }
  };

  return (
    <div className="w-full md:w-45 bg-white p-4 pb-1 border-r border-gray-200 overflow-y-auto">
      {!selectedOrder ? (
        <p className="text-lg text-gray-500">발주를 선택하세요.</p>
      ) : (
        <div className="space-y-1">
          {/* 발주번호 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">발주번호</label>
            <div className="flex-1 flex gap-1">
              <input
                type="text"
                value={selectedOrder.purchaseOrderNo}
                readOnly
                className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
              />
              <button className="w-10 px-2  border border-gray-300 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors flex-shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>

          {/* 상호 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">상호</label>
            <div className="flex-1 flex gap-1">
              <input
                type="text"
                value={selectedOrder.customNm}
                readOnly
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
              />
              <div className="w-10 flex-shrink-0"></div>
            </div>
          </div>

          {/* 입고일자 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">입고일자</label>
            <div className="flex-1 flex gap-1">
              <input
                type="text"
                value={selectedOrder.orderDt}
                readOnly
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
              />
              <div className="w-10 flex-shrink-0"></div>
            </div>
          </div>

          {/* 창고 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">창고</label>
            <div className="flex-1 flex gap-1">
              <input
                type="text"
                value={warehouseNm}
                onClick={openWarehouseSearchModal}
                onKeyDown={handleWarehouseKeyDown}
                readOnly
                autoComplete="off"
                placeholder="창고 선택"
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
              <button
                type="button"
                onClick={openWarehouseSearchModal}
                className="w-10 px-2  border border-gray-300 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors flex-shrink-0"
                title="창고 검색"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scan */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">Scan</label>
            <div className="flex-1 flex gap-1">
              <input
                ref={scanInputRef}
                type="text"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onInput={handleInput}
                onKeyDown={handleScanKeyDown}
                onClick={handleDoubleTap}
                inputMode={keyboardEnabled ? 'text' : 'none'}
                autoComplete="off"
                placeholder="바코드 스캔"
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={toggleKeyboard}
                className={`w-10 flex-shrink-0 px-2  border border-gray-300 rounded transition-colors ${
                  keyboardEnabled 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title={keyboardEnabled ? '키보드 숨기기' : '키보드 표시'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </button>
            </div>
          </div>

          {/* 품명 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">품명</label>
            <div className="flex-1 flex gap-1">
              <input
                type="text"
                value={scannedItem?.itemNm || ''}
                readOnly
                className="flex-1 max-w-[173px] px-2  text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
              />
              <div className="w-10 flex-shrink-0"></div>
            </div>
          </div>

          {/* 입고량 및 버튼들 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">입고량</label>
            <div className="flex-1 flex gap-1">
              <input
                id="transportQty"
                type="number"
                value={transportQty || ''}
                onChange={(e) => setTransportQty(Number(e.target.value))}
                onKeyPress={handleCheckKeyPress}
                placeholder="수량"
                disabled={!scannedItem}
                className="w-20 px-2  text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="0"
                step="0.01"
              />
              <button
                onClick={handleCheck}
                disabled={!scannedItem || !transportQty || !warehouseCd}
                className="flex-1 bg-blue-500 text-white px-2  rounded text-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
              >
                검수 {checkedCount > 0 && `(${checkedCount})`}
              </button>
              <button
                onClick={onComplete}
                disabled={checkedCount === 0}
                className="flex-1 bg-green-500 text-white px-2  rounded text-lg font-medium hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 whitespace-nowrap"
              >
                완료
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDA1040_R01Info;
